/**
 * LAE Productivity metric dictionary: one label and one display format per upstream metric key.
 *
 * Ports LAE `features/productivity/metrics.js` (METRICS, TREND_TALK_KEYS, formatMetric), the
 * `DIRECTION_FILL` swatches from LAE `format.js` and the default series fills from LAE
 * `charts/ColumnChart.jsx`.
 *
 * Deliberate differences from LAE:
 * - Labels only. LAE's `title` definition strings are NOT ported: a definition is helper text, and
 *   the sanctioned home for it is the `?` guide (`lib/guide-content.ts`), not a tooltip.
 * - The rubric-only keys (`productivity_score`, `rating`) and helpers (`metricPhrase`,
 *   `ratingSummary`, `scoreSummary`, `lowerIsBetter`) are dropped with the rubric bands.
 * - Colours are EMERGE token variables (runtime properties with dark overrides in `index.css`), never
 *   the `--color-*` names, which exist only inside the theme block.
 * - Keys stay the upstream snake_case: export column keys and stored column ids equal them.
 */
import { fmtClock, fmtDay, fmtDuration, fmtNum, fmtPct, fmtSentiment } from './lae-format';
import type { LaeProdSeriesPoint } from '@/lib/types';

export type LaeMetricFormat = 'num' | 'pct' | 'duration' | 'sentiment' | 'time';

export interface LaeMetricDef {
  label: string;
  format: LaeMetricFormat;
  /** Decimal places for `pct`; absent = 0. */
  dp?: number;
}

/** Verbatim LAE labels (spec §3.5), keyed by the upstream metric key. */
export const LAE_METRICS: Readonly<Record<string, LaeMetricDef>> = {
  allocated: { label: 'Leads Assigned', format: 'num' },
  covered: { label: 'Leads Contacted', format: 'num' },
  coverage_pct: { label: '% Contacted', format: 'pct' },
  worked: { label: 'Leads Worked On', format: 'num' },
  calls: { label: 'Total Calls Dialled', format: 'num' },
  manual_calls: { label: 'Manual Calls', format: 'num' },
  manual_connected: { label: 'Manual Connected', format: 'num' },
  manual_seconds: { label: 'Manual Talk Time', format: 'duration' },
  connected: { label: 'Calls Answered', format: 'num' },
  connect_rate: { label: 'Answer Rate', format: 'pct' },
  call_seconds: { label: 'Total Call Time', format: 'duration' },
  dial_seconds: { label: 'Total Dial Time', format: 'duration' },
  ring_seconds: { label: 'Ringing Time', format: 'duration' },
  called_records: { label: 'Leads Called', format: 'num' },
  reached_people: { label: 'Leads Spoken To', format: 'num' },
  counselling_completed: { label: 'Marked as Interested in 2027 Intakes', format: 'num' },
  // `advanced` keeps its key for stored column ids; it means a positive-direction status change.
  advanced: { label: 'Positive Status Changes', format: 'num' },
  regressed: { label: 'Negative Status Changes', format: 'num' },
  moved_records: { label: 'Status Changes', format: 'num' },
  applications: { label: 'Applications', format: 'num' },
  whatsapp_prepared: { label: 'Whatsapps Sent', format: 'num' },
  emails_sent: { label: 'Emails Sent', format: 'num' },
  tasks_done: { label: 'Tasks Completed', format: 'num' },
  tasks_overdue: { label: 'Overdue Tasks', format: 'num' },
  active_days: { label: 'Days Active', format: 'num' },
  first_activity_at: { label: 'First Activity', format: 'time' },
  last_activity_at: { label: 'Last Activity', format: 'time' },
  counsellors_active: { label: 'Active Counsellors', format: 'num' },
  assignments: { label: 'Assignments', format: 'num' },
  connected_seconds: { label: 'Talk Time', format: 'duration' },
  avg_talk_seconds: { label: 'Avg Call Time', format: 'duration' },
  cc_calls: { label: 'Calls to Leads Already Interested in 2027 Intakes', format: 'num' },
  cc_talk_seconds: { label: 'Talk Time - Already Interested in 2027 Intakes', format: 'duration' },
  avg_cc_talk_seconds: { label: 'Avg Talk Time - Already Interested in 2027 Intakes', format: 'duration' },
  ccm_calls: { label: 'Answered Calls - Interested in 2027 Intakes', format: 'num' },
  ccm_talk_seconds: { label: 'Total Call Time - Interested in 2027 Intakes', format: 'duration' },
  avg_ccm_talk_seconds: { label: 'Avg Call Time - Interested in 2027 Intakes', format: 'duration' },
  adv_calls: { label: 'Answered Calls - Positive Changes', format: 'num' },
  adv_talk_seconds: { label: 'Total Call Time - Positive Changes', format: 'duration' },
  avg_adv_talk_seconds: { label: 'Avg Call Time - Positive Changes', format: 'duration' },
  neg_calls: { label: 'Answered Calls - Negative Changes', format: 'num' },
  neg_talk_seconds: { label: 'Total Call Time - Negative Changes', format: 'duration' },
  avg_neg_talk_seconds: { label: 'Avg Call Time - Negative Changes', format: 'duration' },
  matriculation: { label: 'Reached Deposit / Visa', format: 'num' },
  analysed_calls: { label: 'Calls Analysed', format: 'num' },
  avg_sentiment: { label: 'Student Sentiment', format: 'sentiment' },
  positive_pct: { label: '% Positive Calls', format: 'pct', dp: 1 },
  hot_leads: { label: 'Hot Leads', format: 'num' },
  quality_score: { label: 'Call Quality', format: 'num' },
  occupancy_pct: { label: 'Occupancy', format: 'pct' },
  available_seconds: { label: 'Available Hours', format: 'duration' },
  busy_seconds: { label: 'Busy Hours', format: 'duration' },
  working_days: { label: 'Working Days', format: 'num' },
};

/** A metric's label; an unknown key reads as the key itself, so a new upstream key is never blank. */
export function metricLabel(key: string): string {
  return LAE_METRICS[key]?.label ?? key;
}

/** null / undefined / '' / non-finite → null; anything else as a number. Never coerces to 0. */
function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Format a metric value by its dictionary format. `time` values (`YYYY-MM-DD HH:mm`, India time)
 * render as `15 Oct 12:11 PM IST`, or the clock alone with `timeOnly` (single-day views). An unknown
 * key formats as a number. Every empty state renders `—`.
 */
export function formatMetric(
  key: string,
  value: number | string | null | undefined,
  options: { timeOnly?: boolean } = {},
): string {
  const def = LAE_METRICS[key];
  switch (def?.format ?? 'num') {
    case 'pct':
      return fmtPct(numberOrNull(value), def?.dp ?? 0);
    case 'sentiment':
      return fmtSentiment(numberOrNull(value));
    case 'duration':
      return fmtDuration(numberOrNull(value));
    case 'time': {
      if (typeof value !== 'string' || value === '') return '—';
      const clock = fmtClock(value);
      if (options.timeOnly || clock === '—') return clock;
      return `${fmtDay(value)} ${clock}`;
    }
    default:
      return fmtNum(numberOrNull(value));
  }
}

/** Trend metric → the [talk seconds, answered calls] series keys behind its hover talk rows. */
export const TREND_TALK_KEYS = {
  calls: ['connected_seconds', 'connected'],
  advanced: ['adv_talk_seconds', 'adv_calls'],
  regressed: ['neg_talk_seconds', 'neg_calls'],
} as const satisfies Readonly<Record<string, readonly [keyof LaeProdSeriesPoint, keyof LaeProdSeriesPoint]>>;

/** Swatch colours for status-change direction (LAE emerald / red → EMERGE success / danger). */
export const DIRECTION_FILL = {
  positive: 'var(--success)',
  negative: 'var(--danger)',
} as const;

/** Default fills for a chart's first and second series (LAE brand 500 / 300 → chart 1 / 2). */
export const SERIES_FILL = ['var(--chart-1)', 'var(--chart-2)'] as const;
